"use client";

/**
 * The listing page's engagement signals (recommender plan, section 2.2):
 *
 *  - `listing.close` {id, ms, by}: visible time on the page (lib/use-listing-dwell);
 *  - `listing.scroll` {id, pct}: 25, 50, 75 and 100% of the page passed,
 *    each once, counted from the first scroll;
 *  - `listing.contact.click` {id, kind}: a click on an element marked
 *    `data-contact="<kind>"` (the "open on the source" button is this
 *    page's contact path).
 *
 * Each carries `rec` and `pos` when the visit came from a recommendation
 * (`?via=digest&rec=<id>&pos=<n>` on the landing URL). Renders nothing;
 * the page stays a server component.
 */
import { useEffect, useState } from "react";
import { captureEvent } from "@/lib/tracking";
import { scrollMarks } from "@/lib/listing-signals";
import { recFromSearch, withRec } from "@/lib/rec-client";
import { useListingDwell } from "@/lib/use-listing-dwell";

export default function ListingPageSignals({ id }: { id: string }) {
  // Read once on the client; never rendered, so no hydration difference.
  const [rec] = useState(() => (typeof window === "undefined" ? null : recFromSearch(window.location.search)));
  useListingDwell(id, rec);

  useEffect(() => {
    const done = new Set<number>();
    let raf = 0;
    const measure = () => {
      raf = 0;
      const doc = document.documentElement;
      for (const pct of scrollMarks(window.scrollY, window.innerHeight, doc.scrollHeight, done)) {
        done.add(pct);
        captureEvent("listing.scroll", withRec({ id, pct }, rec));
      }
      if (done.size === 4) window.removeEventListener("scroll", onScroll);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    window.addEventListener("scroll", onScroll, { passive: true });

    const onClick = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.("[data-contact]") as HTMLElement | null;
      const kind = el?.dataset.contact;
      if (kind) captureEvent("listing.contact.click", withRec({ id, kind }, rec));
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick, { capture: true });
    };
  }, [id, rec]);

  return null;
}
